/**
 * Unwrap RTK Query / proxy errors so dialogs show a human sentence
 * (including 409 path collisions) instead of `[object Object]` or the
 * raw `Proxy POST … failed (409): {json}` dump the BFF sometimes forwards.
 */

const PATH_CONFLICT_FALLBACK =
  'A folder or item already exists at that destination. Choose a different folder, or rename one of them first.'

const PROXY_FAIL = /failed \((\d{3})\):\s*(.*)$/s

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function parseFailedBody(raw: string): {
  status?: number
  message?: string
  reason?: string
  code?: string
} {
  const match = PROXY_FAIL.exec(raw)
  if (!match) return {}
  const status = Number(match[1])
  const rest = match[2].trim()
  try {
    const body: unknown = JSON.parse(rest)
    if (!isRecord(body)) return { status, message: rest }
    return {
      status,
      message: asString(body.message),
      reason: asString(body.reason),
      code: asString(body.code)
    }
  } catch {
    return { status, message: rest || undefined }
  }
}

function looksLikeProxyDump(message: string): boolean {
  return /^Proxy [A-Z]+ /.test(message) || / failed \(\d{3}\):/.test(message)
}

function isPathConflict(reason?: string, code?: string, message?: string): boolean {
  if (reason?.includes('PATH_TAKEN')) return true
  if (code === 'ALREADY_EXISTS' && message !== undefined && /already exists/i.test(message)) {
    return true
  }
  return (
    message !== undefined && /already exists at (the destination path|this path)/i.test(message)
  )
}

export function readMutationError(err: unknown, conflictFallback = PATH_CONFLICT_FALLBACK): string {
  const payload = isRecord(err) ? err : null
  const data = payload && isRecord(payload.data) ? payload.data : null
  let status = typeof payload?.status === 'number' ? payload.status : undefined
  let message = asString(data?.message) ?? asString(data?.error)
  let reason = asString(data?.reason)
  let code = asString(data?.code)

  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : message
  if (raw) {
    const embedded = parseFailedBody(raw)
    if (embedded.status !== undefined) status = status ?? embedded.status
    message = embedded.message ?? message
    reason = embedded.reason ?? reason
    code = embedded.code ?? code
  }

  if (status === 409 || isPathConflict(reason, code, message)) {
    if (!message || looksLikeProxyDump(message) || isPathConflict(reason, code, message)) {
      return conflictFallback
    }
    return message
  }
  if (message && !looksLikeProxyDump(message)) return message
  if (err instanceof Error && !looksLikeProxyDump(err.message)) return err.message
  if (typeof err === 'string' && !looksLikeProxyDump(err)) return err
  return 'Something went wrong.'
}
